// src/components/ChatIA.js
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Lupa } from "./Icones";

// Em produção a função fica no mesmo domínio (functions/api/perguntar.js).
// No `npm start`, src/setupProxy.js encaminha /api para `npm run dev:ia`.
const IA_URL = process.env.REACT_APP_IA_URL || "/api/perguntar";
const OCUPADO = "O assistente gratuito está ocupado agora. Tente de novo em um minuto.";

// Uma falha passageira do servidor (502/503 sem mensagem, conexão caída) ganha
// uma segunda tentativa antes de virar erro na tela.
async function postarNoServidor(corpo) {
  try {
    return await axios.post(IA_URL, corpo);
  } catch (e) {
    const passageira = !e.response || ([502, 503].includes(e.response.status) && !e.response.data?.erro);
    if (!passageira) throw e;
    await new Promise((pronto) => setTimeout(pronto, 1500));
    return axios.post(IA_URL, corpo);
  }
}

// Sem chave no servidor, a função devolve o contexto montado ({ sistema, ia })
// e o navegador chama o modelo direto: assim cada visitante usa a própria
// cota do plano gratuito (ver functions/api/perguntar.js).
async function perguntarAoModelo({ sistema, ia }, conversa) {
  let resp;
  try {
    resp = await axios.post(ia.url, {
      model: ia.modelo,
      temperature: 0.3,
      ...(ia.raciocinio ? { reasoning_effort: ia.raciocinio } : {}),
      messages: [
        { role: "system", content: sistema },
        ...conversa.slice(-10).map(({ role, content }) => ({ role, content })),
      ],
    }, { timeout: 90000 });
  } catch (e) {
    const status = e.response?.status;
    throw new Error(status === 402 || status === 429 ? OCUPADO : "O assistente não respondeu. Tente de novo.");
  }
  if (!resp.data?.choices) throw new Error(OCUPADO);
  const texto = (resp.data.choices[0]?.message?.content || "").replace(/<think>[\s\S]*?<\/think>/g, "").trim();
  if (!texto) throw new Error("O assistente não respondeu. Tente de novo.");
  return texto;
}

// Negrito com **texto** e itálico com *texto*; o resto é texto puro (nada vira HTML).
function Linha({ texto }) {
  return texto.split(/(\*\*[^*]+\*\*|\*[^*\s][^*]*\*)/g).map((parte, i) => {
    if (parte.startsWith("**") && parte.endsWith("**")) return <strong key={i}>{parte.slice(2, -2)}</strong>;
    if (parte.length > 2 && parte.startsWith("*") && parte.endsWith("*")) return <em key={i}>{parte.slice(1, -1)}</em>;
    return <React.Fragment key={i}>{parte}</React.Fragment>;
  });
}

// Parágrafos e listas simples ("- " ou "1. "), que é o que o modelo costuma usar.
function TextoResposta({ texto }) {
  const blocos = [];
  texto.split("\n").forEach((linhaBruta) => {
    const linha = linhaBruta.trim();
    // Os modelos às vezes usam hífens tipográficos (‐ ‑ – —) nos itens.
    const item = linha.match(/^(?:[-*•\u2010-\u2014]|\d+[.)])\s+(.*)$/);
    const ultimo = blocos[blocos.length - 1];
    if (!linha) {
      blocos.push(null);
    } else if (item) {
      if (ultimo && ultimo.tipo === "lista") ultimo.itens.push(item[1]);
      else blocos.push({ tipo: "lista", itens: [item[1]] });
    } else if (ultimo && ultimo.tipo === "paragrafo") {
      ultimo.linhas.push(linha.replace(/^#+\s*/, ""));
    } else {
      blocos.push({ tipo: "paragrafo", linhas: [linha.replace(/^#+\s*/, "")] });
    }
  });
  return blocos.filter(Boolean).map((bloco, i) => (
    bloco.tipo === "lista"
      ? <ul key={i}>{bloco.itens.map((t, j) => <li key={j}><Linha texto={t} /></li>)}</ul>
      : <p key={i}><Linha texto={bloco.linhas.join(" ")} /></p>
  ));
}

// Fontes da busca: só o número e o site; o título e a data aparecem ao
// passar o mouse. Os números batem com os [1], [2] da resposta.
function Fontes({ fontes }) {
  return (
    <div className="chat-fontes" aria-label="Fontes da internet">
      {fontes.map((f, i) => (
        <a key={f.url} className="chat-fonte" href={f.url} target="_blank" rel="noreferrer">
          <b>{i + 1}</b> {f.site}
          <span className="chat-fonte-detalhe" role="tooltip">
            {f.titulo || f.site}
            {f.data && <span>{f.data.split("-").reverse().join("/")}</span>}
          </span>
        </a>
      ))}
    </div>
  );
}

// Uma sugestão é um texto ou { texto, internet: true } para já buscar na internet.
function textoDaSugestao(sugestao) {
  return typeof sugestao === "string" ? sugestao : sugestao.texto;
}

function ChatIA({ tipo, id, sugestoes }) {
  const [mensagens, setMensagens] = useState([]);
  const [rascunho, setRascunho] = useState("");
  const [naInternet, setNaInternet] = useState(false);
  const [buscando, setBuscando] = useState(false);
  const [aguardando, setAguardando] = useState(false);
  const [erro, setErro] = useState(null);
  const fimRef = useRef(null);
  const campoRef = useRef(null);
  const contextoRef = useRef(null);

  // Cada registro tem sua própria conversa.
  useEffect(() => {
    contextoRef.current = null;
    setMensagens([]);
    setRascunho("");
    setErro(null);
  }, [tipo, id]);

  useEffect(() => {
    fimRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [mensagens, aguardando, erro]);

  async function enviar(pergunta, internet = naInternet) {
    const texto = (pergunta ?? rascunho).trim();
    if (!texto || aguardando) return;
    const conversa = [...mensagens, { role: "user", content: texto }];
    const paraServidor = conversa.map(({ role, content }) => ({ role, content }));
    setMensagens(conversa);
    setRascunho("");
    setErro(null);
    setAguardando(true);
    setBuscando(internet);
    try {
      let resposta;
      let extras = {};
      // O contexto guardado não tem busca; com busca, o servidor monta outro.
      if (contextoRef.current && !internet) {
        resposta = await perguntarAoModelo(contextoRef.current, conversa);
      } else {
        const { data } = await postarNoServidor({ tipo, id, mensagens: paraServidor, buscarNaInternet: internet });
        extras = { fontes: data.fontes, avisoBusca: data.avisoBusca };
        if (data.sistema) {
          if (!internet) contextoRef.current = data;
          resposta = await perguntarAoModelo(data, conversa);
        } else {
          resposta = data.resposta;
        }
      }
      setMensagens([...conversa, { role: "assistant", content: resposta, ...extras }]);
    } catch (e) {
      // Erros de perguntarAoModelo já vêm com a mensagem para o visitante.
      setErro(e.response?.data?.erro || (!e.isAxiosError && e.message) || "Não foi possível falar com o assistente agora.");
      // A pergunta volta para o campo, para tentar de novo sem redigitar.
      setMensagens(mensagens);
      setRascunho(texto);
    } finally {
      setAguardando(false);
      setBuscando(false);
      campoRef.current?.focus();
    }
  }

  return (
    <div className="chat">
      <div className="chat-conversa" aria-live="polite">
        {mensagens.length === 0 && (
          <div className="chat-inicio">
            <p>
              Pergunte o que quiser sobre este {tipo === "caso" ? "caso" : "conflito"}.
              O assistente responde com base no registro{tipo === "noticia" ? " e na matéria original" : ""};
              toque na lupa com “+”, à direita do campo de pergunta, para ele pesquisar também na internet.
            </p>
            <div className="chat-sugestoes">
              {sugestoes.map((s) => (
                <button
                  key={textoDaSugestao(s)}
                  type="button"
                  className="chat-sugestao"
                  onClick={() => enviar(textoDaSugestao(s), Boolean(s.internet) || naInternet)}
                >
                  {textoDaSugestao(s)}
                  {s.internet && <span className="chat-sugestao-nota">busca na internet</span>}
                </button>
              ))}
            </div>
          </div>
        )}
        {mensagens.map((m, i) => (
          <div key={i} className={`chat-msg chat-msg-${m.role === "user" ? "pessoa" : "ia"}`}>
            {m.role === "user" ? <p>{m.content}</p> : <TextoResposta texto={m.content} />}
            {m.fontes?.length > 0 && <Fontes fontes={m.fontes} />}
            {m.avisoBusca && <p className="chat-aviso-busca">{m.avisoBusca}</p>}
          </div>
        ))}
        {aguardando && (
          <div className="chat-msg chat-msg-ia chat-pensando" role="status">
            <span className="chat-ponto" /><span className="chat-ponto" /><span className="chat-ponto" />
            {buscando
              ? <span className="chat-pensando-texto">Buscando na internet…</span>
              : <span className="visualmente-oculto">Pensando…</span>}
          </div>
        )}
        {erro && <p className="chat-erro" role="alert">{erro}</p>}
        <div ref={fimRef} />
      </div>

      <form
        className="chat-entrada"
        onSubmit={(e) => {
          e.preventDefault();
          enviar();
        }}
      >
        <div className="chat-campo">
          <textarea
            ref={campoRef}
            rows={1}
            value={rascunho}
            maxLength={1500}
            placeholder={naInternet ? "Pergunte e busque na internet…" : "Escreva sua pergunta…"}
            aria-label="Sua pergunta"
            onChange={(e) => setRascunho(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                enviar();
              }
            }}
          />
          <button
            type="button"
            className="chat-lupa"
            aria-pressed={naInternet}
            aria-label="Buscar também na internet"
            title={naInternet ? "Busca na internet ligada" : "Buscar também na internet"}
            onClick={() => setNaInternet(!naInternet)}
          >
            <Lupa mais />
          </button>
        </div>
        <button type="submit" disabled={!rascunho.trim() || aguardando}>
          Enviar
        </button>
      </form>
      <p className="chat-aviso">Respostas geradas por IA. Confira sempre na fonte.</p>
    </div>
  );
}

export default ChatIA;
